/**
 * Optimistic skill-files store.
 *
 * The skill-files API (`/api/v1/skills/{skillId}/files`) emits no WebSocket
 * events, but AuiFiles relies on the event bus to refresh after upload and
 * delete (it only refetches after *move*). This module-scope observable cache
 * is the shared channel the otherwise-independent adapter hooks
 * (`useEntries` / `useUpload` / `useDelete`) write through, so mutations
 * reflect in the tree immediately. Mirrors the `virtual-folders.ts` pattern.
 *
 * State is keyed by skillId and survives AuiFiles remounts (tree ↔ list view
 * toggle) and sibling instances pointing at the same skill. Files are keyed by
 * `path` — the skill API has no `fileId`. A cold `setAll` (from the list
 * endpoint) is the source of truth; individual mutators keep it in sync between
 * refetches.
 */

import type { SkillFileRef } from '@/types/skill-files';
import { SKILL_FOLDER_CONTENT_TYPE } from '@/types/skill-files';
import type { FilesEntry } from './types';

const filesBySkill = new Map<string, Map<string, FilesEntry>>();
const listeners = new Map<string, Set<() => void>>();

function basename(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx >= 0 ? path.slice(idx + 1) : path;
}

/**
 * Synthesize the `FilesEntry` shape AuiFiles renders from a lean
 * `SkillFileRef`. The skill API carries no `fileId` / timestamps / author —
 * use `path` as the stable row key, the checksum as the etag, and leave
 * timestamps empty (AuiFiles tolerates this; the date column shows nothing).
 */
export function refToEntry(skillId: string, ref: SkillFileRef): FilesEntry {
  return {
    fileId: ref.path,
    ownerId: skillId,
    path: ref.path,
    name: basename(ref.path),
    size: ref.sizeBytes,
    contentType: ref.contentType,
    createdAt: '',
    updatedAt: '',
    createdBy: 'user',
    etag: ref.checksum,
    kind: ref.contentType === SKILL_FOLDER_CONTENT_TYPE ? 'folder' : 'file',
  };
}

function getMap(skillId: string): Map<string, FilesEntry> {
  let m = filesBySkill.get(skillId);
  if (!m) {
    m = new Map();
    filesBySkill.set(skillId, m);
  }
  return m;
}

function notify(skillId: string): void {
  const subs = listeners.get(skillId);
  if (!subs) return;
  for (const fn of subs) fn();
}

export function subscribeSkillFiles(skillId: string, fn: () => void): () => void {
  let subs = listeners.get(skillId);
  if (!subs) {
    subs = new Set();
    listeners.set(skillId, subs);
  }
  subs.add(fn);
  return () => {
    subs!.delete(fn);
  };
}

/** Snapshot of the current entries for a skill. Stable array identity is NOT
 *  guaranteed across calls — callers memoize on the change counter via the
 *  subscribe callback (same contract as `getVirtualFolders`). */
export function getSkillFilesSnapshot(skillId: string): FilesEntry[] {
  return Array.from(getMap(skillId).values());
}

/** Replace the whole set from a fresh list response. Folder refs
 *  (`inode/directory`) are dropped — AuiFiles synthesizes folders from file
 *  paths, and real empty folders aren't persisted by this backend. */
export function setAllSkillFiles(skillId: string, refs: SkillFileRef[]): void {
  const next = new Map<string, FilesEntry>();
  for (const ref of refs) {
    if (ref.contentType === SKILL_FOLDER_CONTENT_TYPE) continue;
    next.set(ref.path, refToEntry(skillId, ref));
  }
  filesBySkill.set(skillId, next);
  notify(skillId);
}

export function upsertSkillFile(skillId: string, ref: SkillFileRef): void {
  if (ref.contentType === SKILL_FOLDER_CONTENT_TYPE) return;
  getMap(skillId).set(ref.path, refToEntry(skillId, ref));
  notify(skillId);
}

export function removeSkillFile(skillId: string, path: string): void {
  const m = getMap(skillId);
  if (m.delete(path)) notify(skillId);
}

/** Remove every file at or under `prefix` (folder cascade delete). */
export function removeSkillFilePrefix(skillId: string, prefix: string): void {
  const m = getMap(skillId);
  let changed = false;
  for (const path of Array.from(m.keys())) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      m.delete(path);
      changed = true;
    }
  }
  if (changed) notify(skillId);
}

/** Re-key entries after a move/rename. Handles both a single file
 *  (`from` === a key) and a folder (every key at/under `from/`). */
export function moveSkillFile(skillId: string, from: string, to: string): void {
  const m = getMap(skillId);
  let changed = false;
  for (const [path, entry] of Array.from(m.entries())) {
    let nextPath: string | null = null;
    if (path === from) {
      nextPath = to;
    } else if (path.startsWith(`${from}/`)) {
      nextPath = `${to}${path.slice(from.length)}`;
    }
    if (nextPath === null) continue;
    m.delete(path);
    m.set(nextPath, {
      ...entry,
      fileId: nextPath,
      path: nextPath,
      name: basename(nextPath),
    });
    changed = true;
  }
  if (changed) notify(skillId);
}
